import { Signer } from "ethers";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  useAccount,
  useBalance,
  useContract,
  useNetwork,
  useSigner,
  useSwitchNetwork,
} from "wagmi";
import { Contracts } from "../config/data";
import { NODE_ENV, Props, WalletContext } from "../types";
import { LOL, LOLExchange, LOLToken, LOLVesting } from "../types/contracts";

const initialState: WalletContext = {
  address: "",
  isOwner: false,
  lolTokenBalance: "0",
  chainId: 0,
  isConnected: false,
  signer: undefined,
  lolTokenContract: undefined,
  lolContract: undefined,
  lolExchangeContract: undefined,
  lolVestingContract: undefined,
};

const lolContractData = Contracts[process.env.NODE_ENV as NODE_ENV]["LOL"];
const lolExchangeContractData =
  Contracts[process.env.NODE_ENV as NODE_ENV]["LOLExchange"];
const lolTokenContractData =
  Contracts[process.env.NODE_ENV as NODE_ENV]["LOLToken"];
const lolVestingData =
  Contracts[process.env.NODE_ENV as NODE_ENV]["LOLVesting"];

const walletContext = createContext<WalletContext>(initialState);

const WalletProvider = ({ children }: Props): JSX.Element => {
  const [isOwner, setIsOwner] = useState<boolean>(false);

  const { address, isConnected } = useAccount();
  const { chain } = useNetwork();
  const { data: signer, isSuccess } = useSigner({
    chainId: lolTokenContractData.chainId,
  });
  const { switchNetwork, isLoading: isSwitching } = useSwitchNetwork({
    chainId: lolTokenContractData.chainId,
  });
  const { data: lolTokenBalance } = useBalance({
    address: address,
    token: lolTokenContractData.address as any,
  });

  const lolContract = useContract({
    address: lolContractData.address,
    abi: lolContractData.abi,
    signerOrProvider: isSuccess ? signer : undefined,
  }) as LOL;

  const lolExchangeContract = useContract({
    address: lolExchangeContractData.address,
    abi: lolExchangeContractData.abi,
    signerOrProvider: isSuccess ? signer : undefined,
  }) as LOLExchange;

  const lolTokenContract = useContract({
    address: lolTokenContractData.address,
    abi: lolTokenContractData.abi,
    signerOrProvider: isSuccess ? signer : undefined,
  }) as LOLToken;

  const lolVestingContract = useContract({
    address: lolVestingData.address,
    abi: lolVestingData.abi,
    signerOrProvider: isSuccess ? signer : undefined,
  }) as LOLVesting;

  const toastId: any = useRef(null);

  useEffect(() => {
    if (
      isConnected &&
      chain?.id !== lolTokenContractData.chainId &&
      switchNetwork &&
      !isSwitching
    ) {
      toastId.current = toast.error("Please switch to the correct network", {
        autoClose: false,
      });
      switchNetwork();
    }
    if (chain?.id === lolTokenContractData.chainId)
      toast.dismiss(toastId.current);
  }, [chain, isConnected, switchNetwork, isSwitching]);

  useEffect(() => {
    if (address && lolTokenContract && isSuccess) {
      lolTokenContract.owner().then((owner) => {
        setIsOwner(owner === address);
      });
    }
  }, [isSuccess, address, lolTokenContract]);

  return (
    <walletContext.Provider
      value={{
        address,
        isOwner,
        lolTokenBalance: lolTokenBalance?.formatted || "0",
        chainId: chain?.id || 0,
        isConnected,
        signer: signer ? (signer as Signer) : undefined,
        lolTokenContract,
        lolContract,
        lolExchangeContract,
        lolVestingContract,
      }}
    >
      {children}
    </walletContext.Provider>
  );
};

export default WalletProvider;

export const useWallet = () => useContext<WalletContext>(walletContext);
